// components/settings/PushNotificationSettings.jsx
// Browser Push Notification Settings

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Bell,
  BellOff,
  Smartphone,
  Monitor,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Mail
} from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../lib/supabaseClient';

export default function PushNotificationSettings() {
  const { user } = useAuth();
  const {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    getSubscriptions,
    removeSubscription
  } = usePushNotifications();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [notificationEmail, setNotificationEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');

  const [notificationCcEmail, setNotificationCcEmail] = useState('');
  const [ccSaving, setCcSaving] = useState(false);
  const [ccSuccess, setCcSuccess] = useState('');
  const [ccError, setCcError] = useState('');

  // Load subscriptions on mount
  useEffect(() => {
    const loadSubscriptions = async () => {
      const subs = await getSubscriptions();
      setSubscriptions(subs);
    };
    loadSubscriptions();
  }, [getSubscriptions, isSubscribed]);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage || actionError) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setActionError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, actionError]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    supabase
      .from('tenants')
      .select('notification_email, notification_cc_email')
      .eq('id', user.tenant_id)
      .single()
      .then(({ data }) => {
        setNotificationEmail(data?.notification_email || '');
        setNotificationCcEmail(data?.notification_cc_email || '');
      });
  }, [user?.tenant_id]);

  const handleSaveEmail = async () => {
    if (!user?.tenant_id) return;
    setEmailSaving(true);
    setEmailError('');
    setEmailSuccess('');
    const { error: err } = await supabase
      .from('tenants')
      .update({ notification_email: notificationEmail || null })
      .eq('id', user.tenant_id);
    setEmailSaving(false);
    if (err) {
      setEmailError('Failed to save: ' + err.message);
    } else {
      setEmailSuccess('Saved!');
      setTimeout(() => setEmailSuccess(''), 3000);
    }
  };

  const handleSaveCcEmail = async () => {
    if (!user?.tenant_id) return;
    setCcSaving(true);
    setCcError('');
    setCcSuccess('');
    const { error: err } = await supabase
      .from('tenants')
      .update({ notification_cc_email: notificationCcEmail || null })
      .eq('id', user.tenant_id);
    setCcSaving(false);
    if (err) {
      setCcError('Failed to save: ' + err.message);
    } else {
      setCcSuccess('Saved!');
      setTimeout(() => setCcSuccess(''), 3000);
    }
  };

  const handleSubscribe = async () => {
    setLoadingAction(true);
    setActionError(null);
    try {
      await subscribe();
      setSuccessMessage('Push notifications enabled for this browser!');
      const subs = await getSubscriptions();
      setSubscriptions(subs);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoadingAction(true);
    setActionError(null);
    try {
      await unsubscribe();
      setSuccessMessage('Push notifications disabled for this browser');
      const subs = await getSubscriptions();
      setSubscriptions(subs);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRemoveDevice = async (subId) => {
    setLoadingAction(true);
    try {
      await removeSubscription(subId);
      setSuccessMessage('Device removed');
      const subs = await getSubscriptions();
      setSubscriptions(subs);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  const getDeviceIcon = (deviceName) => {
    if (
      deviceName?.toLowerCase().includes('mobile') ||
      deviceName?.toLowerCase().includes('android') ||
      deviceName?.toLowerCase().includes('ios')
    ) {
      return Smartphone;
    }
    return Monitor;
  };

  // Email alert card — shared between supported/unsupported paths
  const emailCard = (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-semibold text-slate-900">Hot Lead Email Alerts</h4>
          <p className="text-sm text-slate-500">Where email notifications are sent when a lead goes hot</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="email"
          value={notificationEmail}
          onChange={(e) => setNotificationEmail(e.target.value)}
          placeholder="e.g., sales@yourcompany.com"
          className="flex-1 max-w-sm text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
        />
        <motion.button
          onClick={handleSaveEmail}
          disabled={emailSaving}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {emailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </motion.button>
      </div>
      {emailSuccess && (
        <div className="mt-2 flex items-center space-x-2 text-sm text-emerald-600">
          <CheckCircle className="w-4 h-4" /> <span>{emailSuccess}</span>
        </div>
      )}
      {emailError && (
        <div className="mt-2 flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" /> <span>{emailError}</span>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">
        This is the account default. You can override it per campaign in Campaign Management → Setup.
      </p>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-center space-x-3 mb-3">
          <Mail className="w-4 h-4 text-slate-400" />
          <div>
            <h5 className="text-sm font-semibold text-slate-900">CC Email</h5>
            <p className="text-xs text-slate-500">Copied on every hot lead email above</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="email"
            value={notificationCcEmail}
            onChange={(e) => setNotificationCcEmail(e.target.value)}
            placeholder="e.g., manager@yourcompany.com"
            className="flex-1 max-w-sm text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
          />
          <motion.button
            onClick={handleSaveCcEmail}
            disabled={ccSaving}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {ccSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </motion.button>
        </div>
        {ccSuccess && (
          <div className="mt-2 flex items-center space-x-2 text-sm text-emerald-600">
            <CheckCircle className="w-4 h-4" /> <span>{ccSuccess}</span>
          </div>
        )}
        {ccError && (
          <div className="mt-2 flex items-center space-x-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" /> <span>{ccError}</span>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">
          Account default CC. Per-campaign CC in Campaign Management → Setup overrides this.
        </p>
      </div>
    </div>
  );

  // Not supported
  if (!isSupported) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-slate-400 to-slate-500">
              <BellOff className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Push Notifications</h4>
              <p className="text-sm text-slate-500">Browser notifications for hot leads</p>
            </div>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Not Supported</p>
                <p className="text-xs text-amber-700 mt-1">
                  Your browser does not support push notifications. Try using Chrome, Firefox, or Edge.
                </p>
              </div>
            </div>
          </div>
        </div>
        {emailCard}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Messages */}
      {actionError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{actionError}</span>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700">{successMessage}</span>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${
                isSubscribed ? 'from-emerald-500 to-emerald-600' : 'from-slate-400 to-slate-500'
              }`}
            >
              {isSubscribed ? (
                <Bell className="w-5 h-5 text-white" />
              ) : (
                <BellOff className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Browser Push Notifications</h4>
              <p className="text-sm text-slate-500">Get instant alerts when leads go hot</p>
            </div>
          </div>

          {/* Toggle Button */}
          <motion.button
            onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
            disabled={loading || loadingAction || permission === 'denied'}
            whileTap={{ scale: 0.98 }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isSubscribed
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-emerald-500 text-white hover:bg-emerald-600'
            } ${loading || loadingAction ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading || loadingAction ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSubscribed ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </motion.button>
        </div>

        {/* Permission Denied Warning */}
        {permission === 'denied' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Notifications Blocked</p>
                <p className="text-xs text-red-700 mt-1">
                  You've blocked notifications. Click the lock icon in your address bar and change
                  notifications to "Allow".
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Active Devices */}
        {subscriptions.length > 0 && (
          <div className="space-y-3 mt-4">
            <h5 className="text-sm font-medium text-slate-700">Active Devices</h5>
            <div className="space-y-2">
              {subscriptions.map((sub) => {
                const DeviceIcon = getDeviceIcon(sub.device_name);
                const isCurrentDevice = sub.endpoint === subscription?.endpoint;

                return (
                  <div
                    key={sub.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCurrentDevice
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <DeviceIcon
                        className={`w-5 h-5 ${
                          isCurrentDevice ? 'text-emerald-600' : 'text-slate-400'
                        }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {sub.device_name || 'Unknown Device'}
                          {isCurrentDevice && (
                            <span className="ml-2 text-xs text-emerald-600">(This device)</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          Added {new Date(sub.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {!isCurrentDevice && (
                      <button
                        onClick={() => handleRemoveDevice(sub.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove device"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">What you'll receive:</p>
              <ul className="text-xs space-y-1 list-disc list-inside">
                <li>Instant alerts when leads are marked as hot</li>
                <li>Critical lead escalations requiring immediate attention</li>
                <li>Click notifications to jump directly to the lead</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {emailCard}
    </div>
  );
}
